import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  UncontrolledTooltip,
} from "reactstrap";
import { DropzoneFile, TWENTY_FIVE_MB } from "../../../shared/DropzoneFile";
import { ISource, knowledgeService, sourcesService } from "../../../../services";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { selectorKnowledgeId } from "../../../../redux/chatSlice";
import { RiAttachmentLine } from "@remixicon/react";
import useInterval from "../../../../utils/useInterval";
import { actionDisplayNotification } from "../../../../redux/notificationSlice";
import { useTranslation } from "react-i18next";

interface FileSelectorProps {
  onOpen: () => Promise<void>;
  onLiveUpdating: (status: boolean) => void;
}

const FileSelector = ({ onOpen, onLiveUpdating }: FileSelectorProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const deleteConfirmationMessage = t(
    "pages.projects.components.createEditPage.dropzone.deleteConfirmationMessageChat",
  );
  const inputHelperMessage = t("pages.projects.components.createEditPage.dropzone.inputHelperChat");

  const [sources, setSources] = useState<ISource[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLiveUpdating, setIsLiveUpdating] = useState(false);

  const startLiveUpdating = () => {
    setIsLiveUpdating(true);
    onLiveUpdating(true);
  };

  const stopLiveUpdating = () => {
    setIsLiveUpdating(false);
    onLiveUpdating(false);
  };

  const completedCount = useMemo(
    () => sources.filter((source) => source.processing_status == "processing_complete").length,
    [sources],
  );
  const allFilesProcessed = completedCount == sources.length;

  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => {
    if (!isOpen) {
      onOpen();
    }
    setIsOpen((prev) => !prev);
  };

  const selectedKnowledgeId = useAppSelector(selectorKnowledgeId);

  const fetchSources = async (knowledgeId: number) => {
    const data = await knowledgeService.listSources({
      knowledgeId: knowledgeId,
    });
    setSources(data);
  };

  useInterval(
    async () => {
      if (selectedKnowledgeId) {
        await fetchSources(selectedKnowledgeId);
      }
    },
    isLiveUpdating ? 5000 : null,
  );

  useEffect(() => {
    const is_complete = sources.every(
      (source) => source.processing_status === "processing_complete",
    );
    if (sources.length > 0 && is_complete) {
      stopLiveUpdating();
    }
  }, [sources]);

  useEffect(() => {
    stopLiveUpdating();
    if (selectedKnowledgeId) {
      setIsLoading(true);
      fetchSources(selectedKnowledgeId);
      setIsLoading(false);
    } else {
      setSources([]);
    }
  }, [selectedKnowledgeId]);

  const onDrop = async (acceptedFiles: File[]) => {
    if (sources.length + acceptedFiles.length > 5) {
      dispatch(
        actionDisplayNotification({
          messages: ["Limite de 5 documentos por conversa. Exclua um para anexar outro."],
          severity: "error",
        }),
      );
      return;
    }

    if (selectedKnowledgeId && acceptedFiles.length > 0) {
      setIsLoading(true);
      await sourcesService.uploadSources({
        files: acceptedFiles,
        knowledgeId: selectedKnowledgeId,
      });
      await fetchSources(selectedKnowledgeId);
      setIsLoading(false);
      startLiveUpdating();
    }
  };

  const afterDelete = () => {
    if (selectedKnowledgeId) {
      fetchSources(selectedKnowledgeId);
    }
  };

  // Componente de status dinamico
  let processingStatus = null;
  if (allFilesProcessed && completedCount > 0) {
    processingStatus = (
      <div className="position-absolute top-0 start-100 translate-middle avatar-xxs border rounded-circle bg-danger text-white">
        {completedCount}
      </div>
    );
  }
  if (!allFilesProcessed) {
    processingStatus = (
      <div className="position-absolute top-0 start-100 translate-middle">
        <Spinner size="sm" color="warning" type="grow"></Spinner>
      </div>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
        <ModalHeader toggle={toggle} className="bg-primary bg-opacity-25">
          Documentos Anexados
        </ModalHeader>
        <ModalBody>
          <DropzoneFile
            loading={isLoading}
            accessToDelete={true}
            accessToDownload={false}
            accessToUpload={true}
            maxFileSize={TWENTY_FIVE_MB}
            sources={sources}
            onDrop={onDrop}
            afterDelete={afterDelete}
            deleteConfirmationMessage={deleteConfirmationMessage}
            inputHelperMessage={inputHelperMessage}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggle}>
            Salvar
          </Button>
        </ModalFooter>
      </Modal>
      <>
        <Button color="light" className="btn-icon" id="UploadFileButton" onClick={toggle}>
          {processingStatus}
          <RiAttachmentLine size={20} style={{ color: "#00A793" }} />
        </Button>
        <UncontrolledTooltip target="UploadFileButton" placement="left">
          Upload de documentos
        </UncontrolledTooltip>
      </>
    </>
  );
};

export { FileSelector };
